import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { registerHexLanguage } from '../../utils/monacoHexLanguage';
import { formatXml } from '../../utils/xmlFormat';
import { oneLight, oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
// No static `monaco` import here — every use reads the live instance from the `monaco`
// arg passed to beforeMount/onMount (or monacoRef). Importing the 'monaco-editor' barrel
// would statically re-pull the ~6.5 MB TypeScript language service we removed in setupMonaco.
import { useThemeStore } from '../../store/themeStore';
import { usePreferencesStore } from '../../store/preferencesStore';
import { remeasureMonacoFontsSoon } from '../../utils/monacoFonts';
import { useTranslation } from 'react-i18next';
import { MONACO_LAYOUT_FLUSH_EVENT } from './ResizableSplitter';
import { isDynamicVariable } from '../../utils/dynamicVariables';
import usePointPlacement from '../../hooks/usePointPlacement';
import { registerMonacoThemes, resolveMonacoTheme } from '../../utils/monacoThemes';
import { registerBooScriptCompletions } from '../../utils/booScriptCompletions';
import { lookupVariable } from '../../utils/variableDisplay';
import {
  registerMonacoVariableCompletions, claimMonacoVariableScopes, updateMonacoVariableScopes,
  releaseMonacoVariableScopes,
} from '../../utils/monacoVariableCompletions';
import { textLevelOf, LARGE_TEXT_EDITOR_OPTIONS } from '../../utils/monacoLargeText';
import { variableTokensOf } from './codeEditorViewport';
import { renderCodeEditor } from './codeEditor/renderCodeEditor';

const VAR_DECORATION_CSS_ID = 'monaco-var-decoration-styles';
function injectVarDecorationStyles() {
  if (document.getElementById(VAR_DECORATION_CSS_ID)) return;
  const style = document.createElement('style');
  style.id = VAR_DECORATION_CSS_ID;
  style.textContent = `
    .monaco-editor .monaco-var-found {
      background: rgba(219, 234, 254, 0.75);
      color: #1d4ed8;
      border-radius: 3px;
    }
    .monaco-editor .monaco-var-missing {
      background: rgba(254, 226, 226, 0.75);
      color: #b91c1c;
      border-radius: 3px;
    }
    .monaco-editor.vs-dark .monaco-var-found {
      background: rgba(30, 64, 175, 0.35);
      color: #93c5fd;
      border-radius: 3px;
    }
    .monaco-editor.vs-dark .monaco-var-missing {
      background: rgba(127, 29, 29, 0.45);
      color: #fca5a5;
      border-radius: 3px;
    }
  `;
  document.head.appendChild(style);
}

const CodeEditor = ({
  value,
  onChange,
  language = 'json',
  placeholder: placeholderProp = 'Enter your code here...',
  height = '300px',
  readOnly = false,
  showLineNumbers = true,
  selectedEnvironment,
  globalVariables,
  collectionVariables,
  // Folder chain variables, nearest first (utils/folderVariables).
  folderVariables, workspaceVariables, onVariableUpdate, variableContext,
  /** No outer border, rounded frame, or toolbar (language / format / preview / copy). */
  hideChrome = false,
  /**
   * When set (typically with hideChrome), renders one top row: leading content on the left,
   * Format / Preview / Copy on the right — same actions as the default toolbar (no language chip).
   */
  headerStart = null,
  /**
   * Increment after splitter drag ends so Monaco can re-layout after flex/% heights settle.
   * Parent (e.g. RequestBuilder) bumps this alongside ResizeObserver-driven layout.
   */
  splitLayoutTick = 0,
  /** Called when Ctrl+Enter (Cmd+Enter on Mac) is pressed inside the editor. */
  onCtrlEnter = null,
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const [varTooltip, setVarTooltip] = useState(null);
  // Clamped into the window — see SyntaxHighlightInput, same hint, same bug.
  // Not `varTooltipRef`: that name is taken by the ref mirroring the tooltip
  // STATE for Monaco's click handler, which is a different thing entirely.
  const [varTooltipBoxRef, varTooltipPlace] = usePointPlacement(
    varTooltip ? { x: varTooltip.x + 14, y: varTooltip.y + 6 } : null
  );
  const [editingVariable, setEditingVariable] = useState(null);
  // Past 2 MB / 50k lines the editor drops what scales with the whole text
  // (utils/monacoLargeText). Starts from the value, then follows the model.
  const [textLevel, setTextLevel] = useState(() => textLevelOf(value));
  // The size to name in the notice while a big paste is still on its way in
  // (Monaco inserts it asynchronously; `value` is still the old text then).
  const [incomingChars, setIncomingChars] = useState(0);
  const largeText = textLevel >= 1;
  const largeTextRef = useRef(largeText);
  largeTextRef.current = largeText;
  const textBindingRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  // Ref so the Monaco wrapper's onClick can read the latest tooltip without stale closures.
  const varTooltipRef = useRef(null);
  varTooltipRef.current = varTooltip;
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const editorWrapperRef = useRef(null);
  /** Updated by ResizeObserver on the outer wrapper; triggers useLayoutEffect for Monaco layout. */
  const [hostSize, setHostSize] = useState({ width: 0, height: 0 });
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.theme);
  const editorFontFamily = usePreferencesStore((s) => s.editorFontFamily);
  const editorFontSize   = usePreferencesStore((s) => s.editorFontSize);
  const editorTabSize    = usePreferencesStore((s) => s.editorTabSize);
  const wordWrapPref     = usePreferencesStore((s) => s.wordWrap);
  const stickyScrollPref = usePreferencesStore((s) => s.stickyScroll);
  const placeholder =
    placeholderProp === 'Enter your code here...' ? t('request.enterCode') : placeholderProp;
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const prismStyle = isDark ? oneDark : oneLight;
  const monacoTheme = resolveMonacoTheme(isDark);
  const monacoThemeRef = useRef(monacoTheme);

  /**
   * The scopes in the shape `utils/variableSuggestions` reads, handed to the
   * shared Monaco provider whenever this editor has the caret.
   *
   * The provider is registered once for the whole language, so it cannot close
   * over an editor. The focused one tells it who to offer — and there is only
   * ever one focused editor.
   */
  /** Identity for the shared provider. One per mounted editor, stable. */
  const scopeOwnerRef = useRef({});

  const completionScopes = useMemo(
    () => ({
      folderVariables,
      collectionVariables,
      environmentVariables: selectedEnvironment?.variables,
      workspaceVariables,
      globalVariables,
    }),
    [folderVariables, collectionVariables, selectedEnvironment, workspaceVariables, globalVariables]
  );

  /** Taking the caret means taking the list. */
  const claimVariableScopes = useCallback(() => {
    claimMonacoVariableScopes(scopeOwnerRef.current, completionScopes);
  }, [completionScopes]);

  // Variables arrive asynchronously and change while you type; refresh them
  // for as long as this editor is the one holding the caret. It is a no-op for
  // every editor that is not, which is the point — three of these are mounted
  // per request tab, and two of them (the script editors) resolve nothing.
  useEffect(() => {
    updateMonacoVariableScopes(scopeOwnerRef.current, completionScopes);
  }, [completionScopes]);

  // A closed tab must stop answering for the ones still open.
  useEffect(() => {
    const owner = scopeOwnerRef.current;
    return () => releaseMonacoVariableScopes(owner);
  }, []);

  // Variable scope ref — always current, safe to read from Monaco event callbacks.
  const variableScopesRef = useRef({});
  variableScopesRef.current = {
    folder: folderVariables,
    collection: collectionVariables,
    environment: selectedEnvironment?.variables,
    workspace: workspaceVariables,
    global: globalVariables,
  };

  const decorationsCollectionRef = useRef(null);
  // Stores { range, name, found, value, type } for each active decoration so mouse events can look them up.
  const varDecorationsDataRef = useRef([]);

  // Ref holding the latest decoration-update function so Monaco content-change listeners
  // always call the most recent closure without needing to re-register.
  const updateDecorationsRef = useRef(null);
  updateDecorationsRef.current = () => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco || isPreviewMode) return;
    const model = editor.getModel();
    if (!model) return;

    // A large text is scanned only where it is on screen (re-run on scroll);
    // the hover and click-to-edit only ever need the visible tokens.
    const tokens = variableTokensOf(editor, model, largeTextRef.current);
    const newDecorations = [];
    const newData = [];
    const scopes = variableScopesRef.current;

    for (const token of tokens) {
      const { name } = token;
      const dynamic = isDynamicVariable(name);
      // `value` is what the hover shows (a secret is masked, utils/variableDisplay);
      // click-to-edit reads `rawValue` so saving does not write the mask back.
      const saved = dynamic ? null : lookupVariable(name, scopes);
      const found = dynamic || !!saved;
      const value = dynamic ? 'Computed at request time' : (saved?.display ?? '');
      const rawValue = saved?.value ?? '';
      const type = dynamic ? 'dynamic' : (saved?.type ?? '');

      const startPos = model.getPositionAt(token.start);
      const endPos = model.getPositionAt(token.end);
      const range = new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column);

      newDecorations.push({
        range,
        options: { inlineClassName: found ? 'monaco-var-found' : 'monaco-var-missing' },
      });
      newData.push({
        range, name, found, value, rawValue, type, folderId: saved?.folderId, collectionId: saved?.collectionId,
      });
    }

    varDecorationsDataRef.current = newData;

    if (!decorationsCollectionRef.current) {
      decorationsCollectionRef.current = editor.createDecorationsCollection(newDecorations);
    } else {
      decorationsCollectionRef.current.set(newDecorations);
    }
  };

  const onCtrlEnterRef = useRef(onCtrlEnter);
  onCtrlEnterRef.current = onCtrlEnter;

  // Keep onVariableUpdate accessible from Monaco event callbacks.
  const onVariableUpdateRef = useRef(onVariableUpdate);
  onVariableUpdateRef.current = onVariableUpdate;
  const variableContextRef = useRef(variableContext);
  variableContextRef.current = variableContext;
  const selectedEnvironmentRef = useRef(selectedEnvironment);
  selectedEnvironmentRef.current = selectedEnvironment;

  const saveVariableValue = async (newValue, scope, name) => {
    if (!editingVariable) return;
    const key = name || editingVariable.name;

    // "To Variable": swap the selected text for `{{key}}` in the model, through
    // Monaco's own edit stack so Ctrl+Z puts the original text back. Done before
    // the awaited persistence, so a slow save cannot interleave with the rewrite.
    if (editingVariable.replaceRange && editorRef.current) {
      editorRef.current.executeEdits('toVariable', [
        { range: editingVariable.replaceRange, text: `{{${key}}}`, forceMoveMarkers: true },
      ]);
      editorRef.current.focus();
    }

    const newVariable = { key, value: newValue, enabled: true };
    if (onVariableUpdateRef.current) {
      await onVariableUpdateRef.current(newVariable, {
        scope: scope ?? editingVariable.type,
        collectionId: variableContextRef.current?.collectionId,
        workspaceId: variableContextRef.current?.workspaceId,
      });
    } else {
      window.dispatchEvent(
        new CustomEvent('updateEnvironmentVariable', {
          detail: { variable: newVariable, environment: selectedEnvironmentRef.current },
        })
      );
    }
    setEditingVariable(null);
  };

  // Re-run decorations when variable scopes change.
  useEffect(() => {
    if (updateDecorationsRef.current) updateDecorationsRef.current();
  }, [selectedEnvironment, globalVariables, collectionVariables, folderVariables, workspaceVariables]);

  // Clear the stale collection ref when switching to preview mode so onMount gets a fresh one.
  useEffect(() => {
    if (isPreviewMode) {
      decorationsCollectionRef.current = null;
    }
  }, [isPreviewMode]);

  useEffect(() => {
    injectVarDecorationStyles();
  }, []);

  
// The hex grammar lives in utils/monacoHexLanguage.js (shared with the DB cell editor).

const handleMonacoBeforeMount = (monaco) => {
    monacoRef.current = monaco;
    // Suppress JSON/XML diagnostics — {{variables}} make content temporarily invalid
    if (monaco.languages.json?.jsonDefaults) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({ validate: false });
    }
    registerMonacoThemes(monaco);
    registerHexLanguage(monaco);

    // boo/pm script autocomplete — lightweight custom provider, no TS language worker.
    registerBooScriptCompletions(monaco);

    // `{{…}}` completion. Registered once per Monaco instance because a
    // provider belongs to the LANGUAGE, not to an editor — which is why the
    // variables it offers are handed over separately, by whichever editor has
    // the caret (see utils/monacoVariableCompletions).
    if (!window.__APIBOO_DYN_VAR_PROVIDER__) {
      window.__APIBOO_DYN_VAR_PROVIDER__ = true;
      registerMonacoVariableCompletions(monaco, [
        'json', 'javascript', 'xml', 'html', 'plaintext', 'graphql',
      ]);
    }
  };

  monacoThemeRef.current = monacoTheme;

  useEffect(() => {
    if (monacoRef.current?.editor) {
      monacoRef.current.editor.setTheme(monacoTheme);
    }
  }, [monacoTheme]);

  const applyMonacoLayout = useCallback(() => {
    if (isPreviewMode) return;
    const editor = editorRef.current;
    const wrap = editorWrapperRef.current;
    if (!editor || !wrap) return;
    const rect = wrap.getBoundingClientRect();
    const w = Math.max(0, Math.round(rect.width));
    const h = Math.max(0, Math.round(rect.height));
    if (w < 1 || h < 1) return;
    editor.layout({ width: w, height: h });
  }, [isPreviewMode]);

  const scheduleApplyMonacoLayout = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        applyMonacoLayout();
      });
    });
  }, [applyMonacoLayout]);

  useEffect(() => {
    const onFlush = () => applyMonacoLayout();
    window.addEventListener(MONACO_LAYOUT_FLUSH_EVENT, onFlush);
    return () => window.removeEventListener(MONACO_LAYOUT_FLUSH_EVENT, onFlush);
  }, [applyMonacoLayout]);

  useEffect(() => {
    const el = editorWrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      const w = Math.max(0, Math.round(cr.width));
      const h = Math.max(0, Math.round(cr.height));
      setHostSize((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [isPreviewMode]);

  useLayoutEffect(() => {
    applyMonacoLayout();
  }, [hostSize.width, hostSize.height, splitLayoutTick, isPreviewMode, applyMonacoLayout]);

  useEffect(() => {
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        applyMonacoLayout();
      });
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2 != null) cancelAnimationFrame(id2);
    };
  }, [splitLayoutTick, isPreviewMode, applyMonacoLayout]);

  const formatCode = () => {
    try {
      if (language === 'json') {
        const parsed = JSON.parse(value);
        const formatted = JSON.stringify(parsed, null, 2);
        onChange(formatted);
        // Focus the editor after formatting
        if (editorRef.current) {
          editorRef.current.focus();
        }
      } else if (language === 'xml') {
        // The app's one XML formatter (utils/xmlFormat.js). Malformed XML is
        // left exactly as typed.
        const formatted = formatXml(value);
        if (formatted === null) return;
        onChange(formatted);
        if (editorRef.current) {
          editorRef.current.focus();
        }
      }
    } catch (error) {
      // If formatting fails, keep original value
    }
  };

  // Shift+Alt+F (VS Code's Format Document) runs the same formatter; a ref so
  // the command registered at mount always sees the current value.
  const formatCodeRef = useRef(null);
  formatCodeRef.current = formatCode;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getLanguageForHighlighter = () => {
    switch (language) {
      case 'json': return 'json';
      case 'xml': return 'xml';
      case 'javascript': return 'javascript';
      case 'html': return 'html';
      case 'text': return 'text';
      case 'shell': return 'bash';
      default: return language;
    }
  };

  const getMonacoLanguage = () => {
    switch (language) {
      case 'json': return 'json';
      case 'xml': return 'xml';
      case 'javascript': return 'javascript';
      case 'html': return 'html';
      case 'text': return 'plaintext';
      default: return language;
    }
  };

  const monacoEditorOptions = useMemo(
    () => ({
      readOnly,
      readOnlyMessage: readOnly ? { value: '' } : undefined,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      fontSize: editorFontSize,
      fontFamily: `'${editorFontFamily}', Consolas, Monaco, "Courier New", monospace`,
      lineNumbers: showLineNumbers ? 'on' : 'off',
      wordWrap: wordWrapPref ? 'on' : 'off',
      stickyScroll: { enabled: stickyScrollPref },
      automaticLayout: false,
      tabSize: editorTabSize,
      insertSpaces: true,
      renderLineHighlight: 'line',
      selectOnLineNumbers: true,
      folding: true,
      lineDecorationsWidth: 0,
      lineNumbersMinChars: 3,
      glyphMargin: false,
      scrollbar: {
        vertical: 'visible',
        horizontal: 'auto',
        verticalScrollbarSize: 8,
        horizontalScrollbarSize: 8,
        useShadows: false,
      },
      overviewRulerBorder: false,
      hideCursorInOverviewRuler: true,
      fixedOverflowWidgets: true,
      contextmenu: true,
      // Don't flag ambiguous/invisible/non-ASCII chars (avoids the "ambiguous unicode characters" banner)
      unicodeHighlight: {
        ambiguousCharacters: false,
        invisibleCharacters: false,
        nonBasicASCII: false,
      },
      quickSuggestions: language === 'json' || language === 'javascript',
      suggest: {
        showKeywords: language === 'json',
        showSnippets: false,
      },
      ...(largeText ? LARGE_TEXT_EDITOR_OPTIONS : null),
    }),
    [readOnly, showLineNumbers, language, editorFontFamily, editorFontSize, editorTabSize, wordWrapPref, stickyScrollPref, largeText]
  );

  // The notice row takes height from the editor right after a big paste, which
  // pushed the caret (at the end of the pasted text) just below the fold.
  // Once the new layout is in, bring it back into view.
  useEffect(() => {
    if (!largeText) return undefined;
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        const editor = editorRef.current;
        const pos = editor?.getPosition?.();
        if (!pos || !editor.hasTextFocus?.()) return;
        applyMonacoLayout();
        editor.revealPosition(pos);
      });
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2 != null) cancelAnimationFrame(id2);
    };
  }, [largeText, applyMonacoLayout]);

  // The `value` prop -> the model. Our own onChange echo is recognised by
  // identity, so typing never compares a 10 MB string (./codeEditorText).
  useEffect(() => {
    textBindingRef.current?.applyExternal(value, readOnly);
  }, [value, readOnly]);

  // A font-family or font-size change invalidates Monaco's cached metrics the
  // same way a late-loading webfont does — re-measure or the caret drifts.
  useEffect(() => {
    if (monacoRef.current) remeasureMonacoFontsSoon(monacoRef.current);
  }, [editorFontFamily, editorFontSize]);
  return renderCodeEditor({
    monacoRef, language, monacoTheme, value, placeholder, readOnly, showLineNumbers,
    selectedEnvironment, globalVariables, collectionVariables, folderVariables, workspaceVariables,
    onVariableUpdate, variableContext, hideChrome, headerStart, isPreviewMode, setIsPreviewMode,
    copied, varTooltip, setVarTooltip, varTooltipRef, varTooltipBoxRef, varTooltipPlace,
    editingVariable, setEditingVariable, setTextLevel, incomingChars, setIncomingChars, largeText,
    largeTextRef, textBindingRef, onChangeRef, valueRef, editorRef, editorWrapperRef, isDark,
    prismStyle, monacoThemeRef, claimVariableScopes, varDecorationsDataRef, updateDecorationsRef,
    onCtrlEnterRef, variableContextRef, selectedEnvironmentRef, saveVariableValue,
    handleMonacoBeforeMount, scheduleApplyMonacoLayout, formatCode, formatCodeRef, copyToClipboard,
    getLanguageForHighlighter, getMonacoLanguage, monacoEditorOptions,
  });
};

export default CodeEditor;
